/**
 * useDoctorTier — surfaces a doctor's onboarding tier on /app/cloud.
 *
 * Tier model (per project_summarizenow_doctor_monetization memory):
 *   - "level2" — clinic doctor in users + tenants. Full /app/cloud features.
 *                Source: useAuth().user (snow_session cookie).
 *   - "level1" — D2C doctor in app_users only. Inbox + Shared work; clinic
 *                cards greyed; submits upgrade_request to unlock.
 *                Source: api.authD2c.upgradeStatus() (Clerk Bearer + app_users).
 *   - "pending_review" — submitted L2 application, awaits admin approval.
 *                        Same UI as level1 (greyed) + status banner.
 *   - "rejected" — admin rejected. Banner with reason + retry CTA.
 *   - "anonymous" — neither cookie. Caller decides redirect.
 *
 * Order of precedence: clinic auth wins. If a doctor has BOTH a clinic
 * users row and a D2C app_users row (which can happen when an existing
 * clinic doctor uses the flywheel via a personal email), they get the L2
 * experience.
 *
 * Bridge timing fix (2026-05-22):
 *   Previously on summarisenow.snowmed.health (doctor host), auth/me was
 *   skipped entirely to avoid a console 401. This broke two things:
 *     1. After bridge minted snow_session, mutate("auth/me") was a no-op
 *        (SWR key was null) — clinicUser stayed null forever.
 *     2. CloudHome mounted and fired clinic API calls before bridge resolved,
 *        getting 401s because snow_session wasn't set yet.
 *   Fix: always call auth/me (401 suppressed by .catch); track bridge
 *   completion in state so `loading` stays true until bridge settles,
 *   gating CloudHome's useEffect API calls.
 */
import useSWR, { mutate } from "swr"
import { useEffect, useRef, useState } from "react"
import { useAuth as useClerkAuth } from "@clerk/react"
import { api } from "@/lib/api-client"

export type DoctorTier = "level2" | "level1" | "pending_review" | "rejected" | "anonymous"

export interface DoctorTierState {
  tier:           DoctorTier
  loading:        boolean
  /** L2 only — the clinic users.id */
  userId?:        string
  /** L1/L2 — the D2C app_users.id when present */
  appUserId?:     string
  /** L1 status banner needs the reject reason */
  rejectReason?:  string | null
  /** has the doctor also enabled patient role (controls "Switch to Patient view" menu item) */
  hasPatientRole: boolean
  /** Display name for avatar — clinic displayName preferred, falls back to D2C name */
  displayName?:   string | null
}

export function useDoctorTier(): DoctorTierState {
  const { isLoaded: clerkLoaded } = useClerkAuth()

  // Gate D2C auth on Clerk readiness so no request is sent before Clerk is
  // loaded. Do not require isSignedIn: legacy snow_d2c_session cookie users
  // must still be allowed to authenticate with credentials.
  const shouldFetchD2c = Boolean(clerkLoaded)

  // Always try clinic session — .catch(() => null) suppresses the console 401
  // for L1 doctors who don't have one yet. Previously this was skipped on
  // summarisenow.snowmed.health to hide the 401, but that broke the bridge
  // flow: mutate("auth/me") was a no-op when the key was null.
  const { data: clinicUser, isLoading: clinicLoading } = useSWR(
    "auth/me",
    () => api.auth.me().catch(() => null),
    { revalidateOnFocus: false }
  )

  // Always try D2C session once Clerk is loaded and signed in.
  // revalidateOnMount ensures a fresh check on every mount — critical after OAuth
  // callback (page reload clears SWR cache, but SPA navigation would otherwise serve
  // a stale null from the login page).
  const { data: d2cMe, isLoading: d2cMeLoading } = useSWR(
    shouldFetchD2c ? "authD2c/me" : null,
    () => api.authD2c.me().catch(() => null),
    { revalidateOnFocus: false, revalidateOnMount: true }
  )

  // Only fetch upgrade-status when there's a D2C session.
  const { data: upgrade, isLoading: upgradeLoading } = useSWR(
    d2cMe ? "authD2c/upgrade-status" : null,
    () => api.authD2c.upgradeStatus().catch(() => null),
    { revalidateOnFocus: false }
  )

  // Bridge completion tracking — keeps loading=true until the bridge attempt
  // settles (success or "no_clinic_account"). Without this, CloudHome's
  // useEffect hooks fire clinic API calls before snow_session is set.
  const bridgeAttempted = useRef(false)
  const [bridgeDone, setBridgeDone] = useState(false)

  // Auto-bridge: mint a clinic snow_session for a D2C-authenticated user when
  //   (a) L1→L2 upgrade just landed (status='approved'), OR
  //   (b) D2C email matches a pre-existing clinic users row (the bridge endpoint
  //       does the email-match lookup server-side; we just always try when no
  //       clinic session exists yet).
  // Without (b), pre-existing clinic doctors who log in via D2C OAuth stay in
  // L1 UI forever and clinic-scoped APIs (/v1/sessions etc.) 401.
  useEffect(() => {
    // Already have a clinic session — no bridge needed.
    if (clinicUser?.id) { setBridgeDone(true); return }
    // Wait for Clerk to load.
    if (!clerkLoaded) return
    // Still loading — wait.
    if (clinicLoading || (shouldFetchD2c && d2cMeLoading)) return
    // No D2C session either — nothing to bridge.
    if (!d2cMe?.id) { setBridgeDone(true); return }
    // Already attempted — don't retry.
    if (bridgeAttempted.current) return

    bridgeAttempted.current = true
    api.authD2c.bridgeClinicSession()
      .then((res) => {
        if (res?.ok) mutate("auth/me")   // now key is always "auth/me" — this works
      })
      .catch(() => { /* no clinic account — normal for L1 doctors */ })
      .finally(() => setBridgeDone(true))
  }, [clinicUser?.id, clerkLoaded, clinicLoading, shouldFetchD2c, d2cMeLoading, d2cMe?.id])

  // loading = true until ALL async work (Clerk readiness + fetches + bridge) has settled.
  // CloudHome gates its useEffect API calls on !tier.loading.
  const loading = !clerkLoaded || clinicLoading || (shouldFetchD2c && d2cMeLoading) || upgradeLoading || !bridgeDone
  const hasPatientRole = Boolean(d2cMe?.enabledRoles?.includes("patient"))

  if (clinicUser?.id) {
    return {
      tier: "level2",
      loading,
      userId: String(clinicUser.id),
      appUserId: d2cMe?.id,
      hasPatientRole,
      displayName: (clinicUser as any).displayName ?? d2cMe?.name ?? null,
    }
  }

  if (d2cMe?.id) {
    const status = upgrade?.status ?? "level1"
    // 'approved' (or legacy 'auto_approved') means provisioning succeeded; treat
    // as level2 so the cards unlock immediately even before bridgeClinicSession completes.
    const tier: DoctorTier = (status === "approved" || (status as string) === "auto_approved") ? "level2" : (status as DoctorTier)
    return {
      tier,
      loading,
      appUserId: d2cMe.id,
      rejectReason: upgrade?.reject_reason ?? null,
      hasPatientRole,
      displayName: d2cMe.name ?? null,
    }
  }

  return { tier: "anonymous", loading, hasPatientRole: false, displayName: null }
}
